import type { DialogProps } from '@mui/material/Dialog';
import type { RootState } from 'src/state';

import { gql } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client/react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import SubscriptionUpgradeButton from 'src/pages/dashboard/user/account/subscription/subscription-upgrade-button';
import SubscriptionIntervalSwitch from 'src/pages/dashboard/user/account/subscription/subscription-interval-switch';

import { Iconify } from 'src/components/iconify';

const STRIPE_PRICES_QUERY = gql`
  query GetStripePricesDialog {
    stripePrices {
      monthlyDisplay
      yearlyDisplay
    }
  }
`;

// ----------------------------------------------------------------------

type SubscriptionDialogProps = DialogProps & {
  onClose: () => void;
};

// ----------------------------------------------------------------------

export function SubscriptionDialog({ open, onClose, ...other }: SubscriptionDialogProps) {
  const displayInterval = useSelector((state: RootState) => state.subscription.displayInterval);
  const paymentProvider = useSelector((state: RootState) => state.subscription.paymentProvider);
  const isFastSpring = paymentProvider === 'fastspring';
  const { data: pricesData } = useQuery<{
    stripePrices: { monthlyDisplay: string; yearlyDisplay: string };
  }>(STRIPE_PRICES_QUERY, { skip: isFastSpring });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth {...other}>
      <DialogTitle>
        <Stack alignItems="center" spacing={2}>
          <Typography variant="h4">Upgrade to Educator</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Unlock unlimited content creation and premium features
          </Typography>
          <Box sx={{ mt: 2 }}>
            <SubscriptionIntervalSwitch />
          </Box>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              {isFastSpring ? (
                <span
                  key={`${displayInterval}-pro-price`}
                  data-fsc-item-path={displayInterval === 'monthly' ? 'pro' : 'pro-yearly'}
                  data-fsc-item-price
                >
                  Loading...
                </span>
              ) : (
                <span>
                  {displayInterval === 'monthly'
                    ? (pricesData?.stripePrices?.monthlyDisplay ?? '...')
                    : (pricesData?.stripePrices?.yearlyDisplay ?? '...')}
                </span>
              )}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
            >
              {displayInterval === 'monthly' ? 'per month' : 'per year'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Key Features - Two Column Layout */}
          <Stack direction="row" spacing={3}>
            {/* Left Column */}
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Iconify
                  icon="solar:double-alt-arrow-up-bold-duotone"
                  width={28}
                  sx={{ color: 'primary.main' }}
                />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Unlimited content creation
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Create as many H5P activities as you need
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={2}>
                <Iconify
                  icon="solar:chart-square-outline"
                  width={28}
                  sx={{ color: 'primary.main' }}
                />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Advanced analytics
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Track learner progress with detailed reports
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={2}>
                <Iconify icon="solar:atom-bold-duotone" width={28} sx={{ color: 'primary.main' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    AI Chat Editor
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Create content faster with AI assistance
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            {/* Right Column */}
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Iconify
                  icon="solar:users-group-rounded-bold-duotone"
                  width={28}
                  sx={{ color: 'primary.main' }}
                />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Shared Workspaces
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Collaborate with colleagues on content
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={2}>
                <Iconify
                  icon="solar:inbox-in-bold-duotone"
                  width={28}
                  sx={{ color: 'primary.main' }}
                />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    1 GB upload file limit
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Upload larger files and media assets
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Stack>

          {/* Call to action */}
          <Card sx={{ p: 3, bgcolor: 'primary.lighter', textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Ready to unlock all features?
            </Typography>
            {displayInterval === 'monthly' ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Switch to yearly billing to get 2 months free
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Save 23% with annual billing
              </Typography>
            )}
            <SubscriptionUpgradeButton />
          </Card>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
